import { useEffect, useState } from 'react';
import {
  FilePlus2,
  Pencil,
  Send,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Ban,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/lib/hooks/use-toast';
import { pricerSolarService } from '../services/pricer-solar.service';
import type { PricerSolarHistoryEntry, PricerSolarHistoryType } from '../types/pricer-solar.types';

const TYPE_META: Record<
  PricerSolarHistoryType,
  { icon: typeof FilePlus2; label: string; tone: string }
> = {
  CREATED: { icon: FilePlus2, label: 'Creada', tone: 'text-foreground/80 bg-muted' },
  EDITED: { icon: Pencil, label: 'Editada', tone: 'text-info bg-info-suave' },
  SUBMITTED: { icon: Send, label: 'Enviada a aprobación', tone: 'text-alerta bg-alerta-suave' },
  APPROVED: { icon: CheckCircle2, label: 'Aprobada', tone: 'text-ok bg-ok-suave' },
  REJECTED: { icon: XCircle, label: 'Rechazada', tone: 'text-bloqueo bg-bloqueo-suave' },
  ADJUSTMENT_REQUESTED: {
    icon: AlertTriangle,
    label: 'Ajuste solicitado',
    tone: 'text-alerta bg-alerta-suave',
  },
  CANCELLED: { icon: Ban, label: 'Cancelada', tone: 'text-muted-foreground bg-muted' },
  COMMENT: { icon: MessageSquare, label: 'Comentario', tone: 'text-info bg-info-suave' },
};

function formatValue(v: any): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return String(v);
  return String(v);
}

function formatDate(s: string): string {
  return new Date(s).toLocaleString('es-BO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SolarHistoryTimeline({
  quoteId,
  refreshKey,
  onRefresh,
}: {
  quoteId: string;
  /** cambiá este valor para forzar recarga (e.g. tras editar) */
  refreshKey?: number;
  onRefresh?: () => void;
}) {
  const { toast } = useToast();
  const [entries, setEntries] = useState<PricerSolarHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    pricerSolarService
      .getHistory(quoteId)
      .then(setEntries)
      .catch((err) => {
        toast({
          title: 'Error al cargar historial',
          description: err?.message,
          variant: 'destructive',
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, refreshKey]);

  const handleSend = async () => {
    const trimmed = comment.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      await pricerSolarService.addComment(quoteId, trimmed);
      setComment('');
      load();
      onRefresh?.();
    } catch (err: any) {
      toast({
        title: 'No se pudo guardar el comentario',
        description: err?.response?.data?.error || err?.message,
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          Historial y comentarios
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Cargando…
          </div>
        ) : entries.length === 0 ? (
          <p className="py-6 text-sm text-center text-muted-foreground">Sin actividad aún.</p>
        ) : (
          <ol className="space-y-3 mb-4">
            {entries.map((e) => {
              const meta = TYPE_META[e.type] ?? TYPE_META.COMMENT;
              const Icon = meta.icon;
              return (
                <li key={e.id} className="flex gap-3">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.tone}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-xs font-semibold">{meta.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {e.user.firstName} {e.user.lastName}
                      </span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {formatDate(e.createdAt)}
                      </span>
                    </div>
                    {e.message && (
                      <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{e.message}</p>
                    )}
                    {e.changes && Object.keys(e.changes).length > 0 && (
                      <div className="mt-1.5 rounded-md border bg-muted/30 p-2 text-xs space-y-0.5">
                        {Object.entries(e.changes).map(([k, ch]) => (
                          <div key={k} className="flex gap-1 items-baseline">
                            <span className="font-semibold">{ch.label}:</span>
                            <span className="font-mono text-bloqueo line-through">
                              {formatValue(ch.from)}
                            </span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-mono text-ok">{formatValue(ch.to)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {/* Comment composer */}
        <div className="border-t pt-3 space-y-2">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Escribir un comentario…"
            rows={2}
            className="resize-none text-sm"
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSend} disabled={sending || !comment.trim()}>
              {sending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
              )}
              Comentar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
